// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Which games are played inside the ball once a round has more than one.
 *
 * Mirrors GameScopeCategoryResolver and BallPartitioner in games-service, which is the authority:
 * team, match and hazard games run separately in each ball, everything else across the round.
 * Derived from the definition's own classifiers, so a new game answers the question the moment its
 * competitionType/gameType are set.
 */

type ScopedGame = {
  name?: string | null;
  competitionType?: string | null;
  gameType?: string | null;
};

const equals = (value: string | null | undefined, expected: string): boolean =>
  (value ?? '').trim().toLowerCase() === expected;

/**
 * Nassau's segments and the presses played against them are keyed round-wide, and Irish is played
 * by the whole ball as one team against the other ball — both stay whole-round even though their
 * classifiers say team or match.
 */
function isWholeRoundException(game: ScopedGame): boolean {
  const name = (game.name ?? '').trim().toLowerCase();
  return name === 'nassau' || name === 'irish';
}

export function isPlayedPerBall(game: ScopedGame | null | undefined): boolean {
  if (!game || isWholeRoundException(game)) {
    return false;
  }

  return (
    equals(game.competitionType, 'team') ||
    equals(game.gameType, 'side game') ||
    equals(game.gameType, 'event game')
  );
}

/**
 * The field size a per-ball game's merits are paid off: the biggest ball in the round, or null
 * when the round has only one ball and nothing is scoped.
 *
 * Uneven splits (5 players is 3 + 2) pay two different ladders, and a single row on a card cannot
 * show both — it shows the bigger ball's, where most of the field is.
 */
export function resolveBallMeritPlayerCount(ballSizes: ReadonlyArray<number>): number | null {
  if (ballSizes.length <= 1) {
    return null;
  }

  const biggest = Math.max(...ballSizes);
  return biggest > 0 ? biggest : null;
}
